import { useCallback } from "react";
import { firebaseStorage, FirebaseUploadResult } from "@/utils/api/firebaseStorage";
import { v4 as uuidv4 } from "uuid";

import {
    useAddMediaItem,
    useDeleteMediaItem,
    useDeleteMediaUploadPromise,
    useMediaStore,
    useMediaUploadPromises,
    useSetCurrentMedia,
    useUpdateMediaItem
} from "@/store";
import {
    MediaItemUuid,
    MediaItem,
    MediaItemMetadata,
    MediaItemStatus,
    MediaItemFileType,
    MediaItemValidation,
    MediaItemFile,
    MediaItemUploadPhase
} from "@/types/media";
import {
    extractSingleFrameViaCanvas,
    getVideoMetadata,
    imageDataToFile,
    trimVideo
} from "@utils/videoEditing";
import { getVideoKeyframeOffset } from "@utils/videoKeyframeUtils";
import { getImageDimensions, resizeImage } from "@utils/imageUtils";
import {
    calcClampedTimeForSegment,
    calcFrameCount,
    calcSecondsPerFrame,
    calcSegmentForTargetTime
} from "@utils/videoSegmentation";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@utils/predictions";
import { MAX_VIDEO_DURATION, MAX_VIDEO_DURATION_FLEXIBILITY, OWLVIT_SIZE } from "@/constants/media";
import { useFps } from "@/contexts/FpsContext";
import { getTestMediaSupport } from "./useTestMediaSupport";
import { createSafeBlobUrl, createSafeVideoElement } from "@utils/safeBlobUtils";
import api from "@utils/api";
import { merge } from "lodash";
import { CurrentMedia } from "@/store/slices/mediaSlice";

export function filenameForFile(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
}

export function fileNameForFrame(mediaItemId: MediaItemUuid, frameTime: number): string {
    return `${mediaItemId}_${frameTime}`;
}

export function getExtractedFrameIdsForMediaItem(
    mediaItem?: Pick<MediaItem, "isVideo" | "segmentToFrameMediaItemIdMap">
): MediaItemUuid[] {
    if (!mediaItem || !mediaItem.isVideo) {
        return [];
    }

    return Object.values(mediaItem.segmentToFrameMediaItemIdMap || {}).filter(
        (frameId) => frameId !== undefined
    );
}

export const useMediaUtils = () => {
    const media = useMediaStore();
    const mediaUploadPromises = useMediaUploadPromises();

    const addMediaItem = useAddMediaItem();
    const setCurrentMediaFromStore = useSetCurrentMedia();
    const updateMediaItemFromStore = useUpdateMediaItem();
    const deleteMediaItemFromStore = useDeleteMediaItem();
    const deleteMediaUploadPromise = useDeleteMediaUploadPromise();

    const { fps } = useFps();

    const setCurrentMedia = useCallback(
        (currentMedia?: CurrentMedia, opts?: { skipIfSet: boolean }) => {
            setCurrentMediaFromStore(currentMedia, opts);
        },
        [setCurrentMediaFromStore]
    );

    const updateMediaItem = useCallback(
        (mediaItem: MediaItem) => {
            const { id, ...attrs } = mediaItem;
            updateMediaItemFromStore(id, attrs);
        },
        [updateMediaItemFromStore]
    );

    const addFrameToMediaItem = useCallback(
        ({
            mediaItem,
            frame,
            frameSegment
        }: {
            mediaItem: MediaItem;
            frame: MediaItem;
            frameSegment: number;
        }) => {
            const newMediaItemState: Partial<MediaItem> = {
                segmentToFrameMediaItemIdMap: {
                    ...mediaItem.segmentToFrameMediaItemIdMap,
                    [frameSegment]: frame.id
                }
            };
            if (frameSegment === 0) {
                newMediaItemState.frame0Id = frame.id;
            }

            updateMediaItem({ ...mediaItem, ...newMediaItemState });
        },
        [updateMediaItem]
    );

    const getMediaItemFromName = useCallback(
        (filename: string) => Object.values(media).find((mediaItem) => mediaItem.name === filename),
        [media]
    );

    const reencodeIncompatibleVideo = useCallback(
        async (mediaItem: MediaItem): Promise<MediaItemFile> => {
            const { reencodeVideo } = await import("@utils/api/video");

            let tempUploadResult;

            try {
                // Phase 1: Uploading (0-100% real progress)
                updateMediaItemFromStore(mediaItem.id, {
                    progress: 0,
                    uploadPhase: MediaItemUploadPhase.UPLOADING,
                    uploadPhaseText: "Uploading..."
                });

                const tempFileId = `${mediaItem.id}_original_incompatible`;
                tempUploadResult = await firebaseStorage.uploadFile(
                    mediaItem.file,
                    tempFileId,
                    "anonymous",
                    (uploadProgress) => {
                        updateMediaItemFromStore(mediaItem.id, {
                            progress: uploadProgress,
                            uploadPhase: MediaItemUploadPhase.UPLOADING,
                            uploadPhaseText: "Uploading..."
                        });
                    }
                );

                // Phase 2: Processing (no progress tracking)
                updateMediaItemFromStore(mediaItem.id, {
                    progress: 0,
                    uploadPhase: MediaItemUploadPhase.PROCESSING,
                    uploadPhaseText: "Processing..."
                });

                const reencodeResult = await reencodeVideo({
                    fileId: tempUploadResult.filename,
                    outputFormat: "webm"
                });

                // Phase 3: Downloading processed video (0-100% real progress)
                updateMediaItemFromStore(mediaItem.id, {
                    progress: 0,
                    uploadPhase: MediaItemUploadPhase.DOWNLOADING,
                    uploadPhaseText: "Finishing..."
                });

                const response = await fetch(reencodeResult.storageUrl, {
                    mode: "cors",
                    credentials: "omit"
                });
                if (!response.ok) {
                    throw new Error(`Failed to download re-encoded video: ${response.statusText}`);
                }

                // Track download progress if available
                const contentLength = response.headers.get("content-length");
                const total = contentLength ? parseInt(contentLength, 10) : 0;

                if (total > 0 && response.body) {
                    const reader = response.body.getReader();
                    let receivedLength = 0;
                    const chunks: Uint8Array[] = [];

                    while (true) {
                        const { done, value } = await reader.read();

                        if (done) break;

                        chunks.push(value);
                        receivedLength += value.length;

                        const downloadProgress = Math.round((receivedLength / total) * 100);
                        updateMediaItemFromStore(mediaItem.id, {
                            progress: downloadProgress,
                            uploadPhase: MediaItemUploadPhase.DOWNLOADING,
                            uploadPhaseText: "Finishing..."
                        });
                    }

                    // Combine chunks into blob
                    const chunksAll = new Uint8Array(receivedLength);
                    let position = 0;
                    for (const chunk of chunks) {
                        chunksAll.set(chunk, position);
                        position += chunk.length;
                    }

                    const blob = new Blob([chunksAll], {
                        type: response.headers.get("content-type") || "video/webm"
                    });

                    // Create new File object
                    const reencodedFile = new File(
                        [blob],
                        mediaItem.file.name.replace(/\.[^.]+$/, ".webm"),
                        { type: "video/webm" }
                    ) as MediaItemFile;

                    // Clear progress indicators
                    updateMediaItemFromStore(mediaItem.id, {
                        progress: 100,
                        uploadPhase: undefined,
                        uploadPhaseText: undefined
                    });

                    return reencodedFile;
                } else {
                    // Fallback: no progress tracking for download
                    const blob = await response.blob();

                    const reencodedFile = new File(
                        [blob],
                        mediaItem.file.name.replace(/\.[^.]+$/, ".webm"),
                        { type: "video/webm" }
                    ) as MediaItemFile;

                    // Clear progress indicators
                    updateMediaItemFromStore(mediaItem.id, {
                        progress: 100,
                        uploadPhase: undefined,
                        uploadPhaseText: undefined
                    });

                    return reencodedFile;
                }
            } finally {
                // Clean up temp file (always runs, even on error)
                if (tempUploadResult?.filename) {
                    try {
                        await firebaseStorage.deleteFile(tempUploadResult.filename);
                    } catch (err) {
                        console.warn("Failed to clean up temp file:", err);
                    }
                }
            }
        },
        [updateMediaItemFromStore]
    );

    const mediaItemMetadataFactoryGenerate = useCallback(
        async (mediaItem: MediaItem) => {
            let metadata: MediaItemMetadata;

            if (mediaItem.isVideo) {
                // For all videos, run through re-encoding check (backend will check codec + fps)
                // This ensures videos are checked for both codec AND fps compatibility
                const reencodedFile = await reencodeIncompatibleVideo(mediaItem);
                const file = reencodedFile;

                // Update the media item with the new file (may be same as original if no re-encoding needed)
                mediaItem.file = file;

                // Get metadata from the (potentially re-encoded) video
                const videoMetadata = await getVideoMetadata(file, fps);
                const frameCount = calcFrameCount(videoMetadata.duration, videoMetadata.fps);
                metadata = {
                    ...mediaItem.metadata,
                    frame_count: frameCount,
                    videoMetadata,
                    serverMediaDimensions: videoMetadata.dimensions
                } as MediaItemMetadata;
            } else {
                const imageDimensions = await getImageDimensions(mediaItem.file);

                metadata = {
                    ...mediaItem.metadata,
                    frame_count: 1,
                    serverMediaDimensions: imageDimensions
                } as MediaItemMetadata;
            }

            return metadata;
        },
        [fps, reencodeIncompatibleVideo]
    );

    const validateMediaItem = useCallback(async (mediaItem: MediaItem) => {
        const file = mediaItem.file;
        const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;

        let validation: MediaItemValidation = { isValid: true, needsTrimming: false, errors: [] };

        const supportedMedia = await getTestMediaSupport();
        const shouldBeSupported =
            supportedMedia.allExtensionsSupported.includes(extension) ||
            supportedMedia.allMimeTypesSupported.includes(file.type);

        if (file.type.startsWith("image/")) {
            // truly test if the image can be loaded by the browser
            const img = new Image();
            const safeUrl = createSafeBlobUrl(file);
            const isValid = await new Promise<boolean>((resolve) => {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = safeUrl.url ?? "invalid";
            });

            safeUrl.cleanup();

            if (!isValid) {
                validation = {
                    isValid: false,
                    needsTrimming: false,
                    errors: [
                        shouldBeSupported
                            ? `The image '${file.name}' seems corrupted or is unsupported by your browser.`
                            : `Invalid image type ${extension} (${file.type}). Your browser supports: ${supportedMedia.imageExtensionsSupported.join(", ")}`
                    ]
                };
            }
        } else if (file.type.startsWith("video/")) {
            // truly test if the video can be loaded by the browser
            const video = await createSafeVideoElement(file);
            validation = await new Promise((resolve) => {
                video.element.onloadedmetadata = () => {
                    const duration = video.element.duration;

                    // Check if duration exceeds max allowed duration while considering keyframes are not perfect
                    const needsTrimming =
                        duration > MAX_VIDEO_DURATION * (1 + MAX_VIDEO_DURATION_FLEXIBILITY);

                    resolve({ isValid: true, needsTrimming, errors: [] });
                };
                video.element.onerror = () => {
                    // For incompatible videos, mark as valid - they will be re-encoded
                    resolve({ isValid: true, needsTrimming: false, errors: [] });
                };
            });
            video.cleanup();
        } else {
            validation = {
                isValid: false,
                needsTrimming: false,
                errors: [
                    `Unsupported file type ${extension} (${file.type}). Supported types: ${supportedMedia.allExtensionsSupported.join(", ")}`
                ]
            };
        }

        return validation;
    }, []);

    const videoMediaItemSegmentToFrameMediaItemIdMapFactoryGenerate = useCallback(
        (mediaItem: MediaItem) => {
            const metadata = mediaItem.metadata!;
            const frameCount = metadata.frame_count;
            const { fps } = metadata.videoMetadata!;
            const segmentToFrameMediaItemIdMap: Record<number, MediaItemUuid | undefined> = {};
            const secondsPerFrame = calcSecondsPerFrame(fps);
            for (let i = 0; i < frameCount; i++) {
                const segment = i * secondsPerFrame; // 0/30, 1/30 ... 30/30, ... 455/30
                // Each segment key is the start time for the frame (in seconds)
                segmentToFrameMediaItemIdMap[segment] = undefined;
            }

            return segmentToFrameMediaItemIdMap;
        },
        []
    );

    const mediaItemFactoryGenerate = useCallback(
        async ({ file, progress = 0, ...attrs }: { file: MediaItemFile } & Partial<MediaItem>) => {
            if (!file) {
                throw new Error("File is required");
            }

            // Check MIME type first, then fallback to file extension for formats without MIME
            let isVideo = file.type.startsWith("video/");
            if (!isVideo) {
                const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
                const videoExtensions = [
                    ".mp4",
                    ".webm",
                    ".mov",
                    ".avi",
                    ".mkv",
                    ".wmv",
                    ".flv",
                    ".m4v",
                    ".3gp",
                    ".3g2",
                    ".ogv",
                    ".ts",
                    ".mts",
                    ".asf",
                    ".divx",
                    ".dv",
                    ".rv",
                    ".vob"
                ];
                isVideo = videoExtensions.includes(extension);
            }
            const fileType = isVideo ? MediaItemFileType.VIDEO : MediaItemFileType.IMAGE;

            let mediaItem: MediaItem = {
                id: file.id || uuidv4(),
                uploadResult: file.uploadResult,
                uploadResizedResult: file.uploadResizedResult,
                status:
                    file.uploadResult || file.uploadResizedResult
                        ? MediaItemStatus.COMPLETED
                        : MediaItemStatus.PENDING,
                file,
                fileType,
                isTestFile: false,
                name: filenameForFile(file),
                storageUploadDirOverride: file.storageUploadDirOverride,
                progress,
                confidenceThreshold: DEFAULT_CONFIDENCE_THRESHOLD,
                isVideo,
                metadata: { frame_count: 1, clamped_time: 0, segment: 0, keyframe_offset: 0 },
                ...attrs
            };

            mediaItem.metadata = await mediaItemMetadataFactoryGenerate(mediaItem);

            if (mediaItem.isVideo) {
                // initialize the segmentToFrameMediaItemIdMap
                mediaItem.segmentToFrameMediaItemIdMap =
                    videoMediaItemSegmentToFrameMediaItemIdMapFactoryGenerate(mediaItem);
            }

            if (!mediaItem.validation) {
                mediaItem.validation = await validateMediaItem(mediaItem);
            }

            if (mediaItem.validation && !mediaItem.validation.isValid) {
                mediaItem.error = new Error(mediaItem.validation.errors.join(", "));
            }

            if (mediaItem.error) {
                mediaItem.status = MediaItemStatus.ERROR;
            } else if (mediaItem.status === MediaItemStatus.PROCESSING) {
                // If still processing after all initialization (including re-encoding), mark as ready
                mediaItem.status = MediaItemStatus.PENDING;
            }

            return mediaItem;
        },
        [
            mediaItemMetadataFactoryGenerate,
            validateMediaItem,
            videoMediaItemSegmentToFrameMediaItemIdMapFactoryGenerate
        ]
    );

    const extractFrameFileFromMediaItem = useCallback(
        async ({ mediaItem, targetTime }: { mediaItem: MediaItem; targetTime: number }) => {
            if (!mediaItem.isVideo) {
                throw new Error("Can only extract frames from video files");
            }

            try {
                if (!mediaItem.metadata?.videoMetadata?.dimensions) {
                    throw new Error("No video dimensions found");
                }

                // Use the current inference FPS for frame extraction
                // This ensures we extract frames at the right times for the current FPS setting
                const videoFps = fps || mediaItem.metadata.videoMetadata?.fps || 30;
                const secondsPerFrame = calcSecondsPerFrame(videoFps);

                // check if the frame already exists to avoid duplicate extraction
                const existingSegmentFrameId =
                    mediaItem.segmentToFrameMediaItemIdMap![
                        calcSegmentForTargetTime(targetTime, secondsPerFrame)
                    ];
                let existingFrameMediaItem: MediaItem | undefined;
                if (existingSegmentFrameId) {
                    existingFrameMediaItem = media[existingSegmentFrameId];

                    if (existingFrameMediaItem) {
                        return existingFrameMediaItem;
                    }
                }

                // extract the frame

                const segment = calcSegmentForTargetTime(targetTime, secondsPerFrame);
                const clampedTime = calcClampedTimeForSegment(segment, secondsPerFrame);

                const imageData = await extractSingleFrameViaCanvas(
                    mediaItem.file,
                    clampedTime,
                    mediaItem.metadata?.videoMetadata?.dimensions,
                    videoFps
                );

                const frameFilename = fileNameForFrame(mediaItem.id, clampedTime);
                const file = await imageDataToFile(imageData, frameFilename);
                const frameFileName = filenameForFile(file);

                let frameMediaItem = getMediaItemFromName(frameFileName);
                if (frameMediaItem) {
                    return frameMediaItem;
                }

                const newFrameMediaItem = await mediaItemFactoryGenerate({
                    file,
                    name: frameFilename,
                    metadata: {
                        frame_count: 1,
                        clamped_time: clampedTime,
                        segment: calcSegmentForTargetTime(clampedTime, secondsPerFrame)
                    }
                });

                addMediaItem(newFrameMediaItem);

                addFrameToMediaItem({
                    mediaItem: mediaItem,
                    frame: newFrameMediaItem,
                    frameSegment: segment
                });

                return newFrameMediaItem;
            } catch (err) {
                throw new Error("Error extracting frame", { cause: err });
            }
        },
        [
            fps,
            media,
            getMediaItemFromName,
            mediaItemFactoryGenerate,
            addFrameToMediaItem,
            addMediaItem
        ]
    );

    const trimMediaItemFile = useCallback(
        async ({
            mediaItem,
            startTime,
            endTime
        }: {
            mediaItem: MediaItem;
            startTime: number;
            endTime: number;
        }) => {
            try {
                const trimmingMediaItem = merge({}, mediaItem, {
                    metadata: { videoMetadata: { isTrimming: true } }
                }) as MediaItem;

                updateMediaItem(trimmingMediaItem);

                const trimmedFile = await trimVideo({
                    file: trimmingMediaItem.file,
                    startTime,
                    endTime,
                    fpsRate: fps,
                    fastMode: true
                });

                let trimmedMediaItem: MediaItem = { ...mediaItem, file: trimmedFile };
                // don't use merge here because we want to override the metadata and validation
                trimmedMediaItem = {
                    ...trimmedMediaItem,
                    status: MediaItemStatus.PENDING, // set back to pending to trigger upload
                    metadata: {
                        ...(await mediaItemMetadataFactoryGenerate(trimmedMediaItem)),
                        keyframe_offset: await getVideoKeyframeOffset(trimmedFile)
                    },
                    validation: { isValid: true, needsTrimming: false, errors: [] }
                };

                // refresh metadata with newly trimmed file, keyframe offset, and validation
                updateMediaItem(trimmedMediaItem);

                return trimmedMediaItem;
            } catch (err) {
                updateMediaItem({
                    ...mediaItem,
                    error: err as Error,
                    status: MediaItemStatus.ERROR
                });

                throw new Error("Error trimming video. Please try again.");
            }
        },
        [fps, mediaItemMetadataFactoryGenerate, updateMediaItem]
    );

    const uploadMediaItem = useCallback(
        async (
            mediaItem: MediaItem,
            opts: { storageUploadDir: string },
            callbacks?: {
                onStarted?: (mediaItem: MediaItem) => void;
                onSuccess?: (mediaItem: MediaItem) => void;
            }
        ) => {
            const storageUploadDir = mediaItem.storageUploadDirOverride || opts.storageUploadDir;

            // check if we need to resize the image/videoframe
            const { width = 1, height = 1 } =
                mediaItem.metadata?.videoMetadata?.dimensions ||
                mediaItem.metadata?.serverMediaDimensions ||
                mediaItem.metadata?.naturalDimensions ||
                {};

            let resizeRatio = Math.min(1, OWLVIT_SIZE / width, OWLVIT_SIZE / height);

            try {
                let uploadPromise: Promise<FirebaseUploadResult[]> | undefined =
                    mediaUploadPromises[mediaItem.id];

                // prevent a double upload, allow to await
                if (!uploadPromise) {
                    mediaItem = {
                        ...mediaItem,
                        startTime: Date.now(),
                        progress: 50, // TODO: this is arbitrary for now
                        status: MediaItemStatus.UPLOADING
                    };

                    updateMediaItem(mediaItem);

                    callbacks?.onStarted?.(mediaItem);

                    let uploads = [
                        firebaseStorage.uploadFile(mediaItem.file, mediaItem.id, storageUploadDir)
                    ];

                    // if an image/frame, also upload a resized version for sam/owlvit/inference use
                    if (!mediaItem.isVideo && resizeRatio < 1) {
                        uploads.push(
                            (async () => {
                                let resizedFile: File;
                                try {
                                    resizedFile = await resizeImage(
                                        mediaItem.file,
                                        OWLVIT_SIZE,
                                        OWLVIT_SIZE
                                    );
                                } catch (err) {
                                    console.error(
                                        "Error resizing image, skipping resized upload",
                                        err
                                    );
                                    // as a fallback, use the original file
                                    resizedFile = mediaItem.file;
                                }

                                const uploadResult = await firebaseStorage.uploadFile(
                                    resizedFile,
                                    `${mediaItem.id}/size_max${OWLVIT_SIZE}`,
                                    storageUploadDir
                                );
                                return uploadResult;
                            })()
                        );
                    }

                    uploadPromise = Promise.all(uploads);
                }

                const results = await uploadPromise;

                // Store upload result with download URL and filename
                if (results[0]) {
                    const uploadResult = results[0];
                    mediaItem.uploadResult = {
                        downloadUrl: uploadResult.downloadUrl,
                        filename: uploadResult.filename
                    };

                    // store the resize ratio if applicable for videos.
                    // For images, it's stored on the resized result (uploadResizedResult)
                    if (mediaItem.isVideo) {
                        mediaItem.uploadResult.resizeRatio = resizeRatio;
                    }
                }

                // store resized upload result if applicable
                if (results[1]) {
                    const resizedResult = results[1];
                    mediaItem.uploadResizedResult = {
                        downloadUrl: resizedResult.downloadUrl,
                        filename: resizedResult.filename,
                        resizeRatio: resizeRatio
                    };
                }

                mediaItem = { ...mediaItem, progress: 100, status: MediaItemStatus.COMPLETED };

                updateMediaItem(mediaItem);

                deleteMediaUploadPromise(mediaItem.id);

                callbacks?.onSuccess?.(mediaItem);

                return mediaItem;
            } catch (err) {
                deleteMediaUploadPromise(mediaItem.id);

                mediaItem = { ...mediaItem, error: err as Error, status: MediaItemStatus.ERROR };

                updateMediaItem(mediaItem);

                throw new Error("Error uploading file", { cause: err });
            }
        },
        [mediaUploadPromises, updateMediaItem, deleteMediaUploadPromise]
    );

    const deleteMediaItem = useCallback(
        async (mediaItemId: MediaItemUuid, opts?: { keepInStorage?: boolean }) => {
            const mediaItem = media[mediaItemId];
            if (!mediaItem) {
                throw new Error(`Media item not found for id ${mediaItemId}`);
            }

            try {
                // Only delete from Firebase Storage if:
                // 1. Not explicitly keeping in storage
                // 2. File was uploaded (not skipUpload - e.g., Improve flow CDN images)
                // 3. There's a valid filename to delete
                const shouldDeleteFromStorage =
                    !opts?.keepInStorage &&
                    !mediaItem.file.skipUpload &&
                    mediaItem.uploadResult?.filename;

                if (shouldDeleteFromStorage) {
                    await firebaseStorage.deleteFile(mediaItem.uploadResult?.filename!);
                }
                deleteMediaUploadPromise(mediaItemId);
                deleteMediaItemFromStore(mediaItemId);
                return mediaItem;
            } catch (err) {
                updateMediaItem({
                    ...mediaItem,
                    error: err as Error,
                    status: MediaItemStatus.ERROR
                });

                throw new Error("Error deleting media item", { cause: err });
            }
        },
        [media, deleteMediaUploadPromise, deleteMediaItemFromStore, updateMediaItem]
    );

    const getMediaItemCaptionProposals = useCallback(
        async (mediaItem: MediaItem, opts?: { abortController: AbortController }) => {
            // return previously fetched captions
            if (mediaItem.fileCaption) return mediaItem.fileCaption;

            // For hosted images (skipUpload=true), use the image URL directly
            const hasImageUrl = mediaItem.file.skipUpload && mediaItem.file.imageUrl;

            // use the resized upload if available (for non-hosted images)
            const upload = mediaItem.uploadResizedResult || mediaItem.uploadResult;

            if (
                !hasImageUrl &&
                (mediaItem.status !== MediaItemStatus.COMPLETED || !upload?.filename)
            ) {
                console.warn(
                    `Skipping captions for file ${mediaItem.file.name} - not uploaded yet`
                );
                return;
            }

            const captionPayload = {
                // For hosted images, use the mediaItem.id; backend will use imageUrl
                fileId: hasImageUrl ? mediaItem.id : upload!.filename,
                // Pass image URL directly to backend so it doesn't need to look up in storage
                imageUrl: hasImageUrl ? mediaItem.file.imageUrl : undefined
            };

            mediaItem.fileCaption = await api.inference.postCaptionInference(
                captionPayload,
                opts?.abortController
            );

            updateMediaItem(mediaItem);

            return mediaItem.fileCaption;
        },
        [updateMediaItem]
    );

    return {
        setCurrentMedia,
        addMediaItem,
        updateMediaItem,
        mediaItemFactoryGenerate,
        addFrameToMediaItem,
        extractFrameFileFromMediaItem,
        trimMediaItemFile,
        validateMediaItem,
        uploadMediaItem,
        deleteMediaItem,
        getMediaItemCaptionProposals
    };
};
